import { isWithheld, type Figure } from "../api";

/**
 * A figure the server may have withheld.
 *
 * Three states, and conflating any two of them misreports the record:
 *   a number      — show it
 *   null          — the record holds no figure
 *   {withheld}    — there is one, and this reader may not see it
 *
 * The last two used to render as the same em dash, differing only in a
 * tooltip. That quietly undid the distinction serialise.py exists to keep:
 * on the Supervisor's radar, six dashes meant withheld and three meant
 * genuinely nil, and the note above the table told them every dash meant
 * withheld. A tooltip is the wrong place for this — it needs a hover per
 * cell, and it survives neither a screenshot nor a printout nor being read
 * aloud.
 *
 * So they are different marks. The em dash keeps its ordinary meaning,
 * nothing recorded. Withheld is a redaction bar, because that is what it
 * is: distinct at a glance, and carrying no digits for anyone to read off.
 */
export function Money({ value }: { value: Figure }) {
  if (isWithheld(value)) {
    return (
      <span
        className="redacted"
        role="img"
        aria-label="Withheld — your role does not include financial visibility"
        title="Withheld — your role does not include financial visibility"
      />
    );
  }
  if (value === null || value === undefined) {
    return <span className="withheld" title="No figure recorded">—</span>;
  }
  return <>£{value.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</>;
}

/** Days to a deadline, in words a reader does not have to decode. */
export function Days({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <>—</>;
  if (value < 0) return <span style={{ color: "var(--red)" }}>{Math.abs(value)}d over</span>;
  if (value === 0) return <>today</>;
  return <>{value}d</>;
}

/** An enum or snake_case string, as human text. */
export function label(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => (["ce", "pm", "ew", "nec4"].includes(w.toLowerCase()) ? w.toUpperCase() : w))
    .join(" ");
}
